import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Wrench, AlertTriangle, CheckCircle2, Clock, Loader2, Trash2 } from "lucide-react";
import { WorkOrder } from "@/hooks/use-property-management";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { WorkOrderDetailDrawer } from "./WorkOrderDetailDrawer";
import { PropertyMeta } from "@/lib/property-meta";
import { rentedListingPropertyOptions } from "@/hooks/use-properties-with-rented-listing";
import { useRentals } from "@/hooks/use-rentals";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface Props {
  workOrders: WorkOrder[];
  isLoading: boolean;
  portfolioProperties: { id: string; label: string }[];
  propertyMeta: Record<string, PropertyMeta>;
  onCreate: (wo: Partial<WorkOrder>) => void;
  onUpdate: (wo: Partial<WorkOrder> & { id: string }) => void;
  /**
   * Permanently removes the work order. `onSuccess` fires only when the delete
   * lands, so a failed one leaves the confirmation open (the toast explains why).
   */
  onDelete: (id: string, opts: { onSuccess: () => void }) => void;
  isSaving: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const STATUS_BADGES: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; className?: string; icon: React.ReactNode }> = {
  open: { variant: "destructive", icon: <AlertTriangle className="h-3 w-3" /> },
  in_progress: { variant: "outline", className: "bg-warning/15 text-warning border-transparent", icon: <Clock className="h-3 w-3" /> },
  completed: { variant: "outline", className: "bg-success/15 text-success border-transparent", icon: <CheckCircle2 className="h-3 w-3" /> },
};

const CATEGORIES = ["general", "plumbing", "electrical", "hvac", "appliance", "roofing", "landscaping", "pest_control", "other"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

export function WorkOrdersTab({ workOrders, isLoading, portfolioProperties, propertyMeta, onCreate, onUpdate, onDelete, isSaving, isUpdating, isDeleting }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<WorkOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkOrder | null>(null);
  const [filterPropertyId, setFilterPropertyId] = useState("all");
  const [form, setForm] = useState<Partial<WorkOrder>>({
    title: "", description: "", category: "general", priority: "medium", status: "open",
  });

  const { listings } = useRentals();
  const workOrderPropertyOptions = useMemo(
    () => rentedListingPropertyOptions(portfolioProperties, listings),
    [portfolioProperties, listings],
  );

  // Filter options: one entry per property that has work orders, labeled by the
  // listing title when one exists, otherwise the property address.
  const filterOptions = useMemo(() => {
    const listingTitleByProperty = new Map<string, string>();
    for (const l of listings) {
      if (!listingTitleByProperty.has(l.pipeline_property_id)) listingTitleByProperty.set(l.pipeline_property_id, l.title);
    }
    const seen = new Set<string>();
    const options: { id: string; label: string }[] = [];
    for (const wo of workOrders) {
      if (seen.has(wo.pipeline_property_id)) continue;
      seen.add(wo.pipeline_property_id);
      const label = listingTitleByProperty.get(wo.pipeline_property_id)
        ?? propertyMeta[wo.pipeline_property_id]?.address
        ?? `Property ${wo.pipeline_property_id.slice(0, 6)}`;
      options.push({ id: wo.pipeline_property_id, label });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders, listings, propertyMeta]);

  const visibleWorkOrders = useMemo(
    () => (filterPropertyId === "all" ? workOrders : workOrders.filter((w) => w.pipeline_property_id === filterPropertyId)),
    [workOrders, filterPropertyId],
  );

  // Keep the open drawer in sync with list refetches.
  useEffect(() => {
    if (!selected) return;
    const updated = workOrders.find((w) => w.id === selected.id);
    if (updated && updated !== selected) setSelected(updated);
  }, [workOrders, selected]);

  const handleSubmit = () => {
    if (!form.title || !form.pipeline_property_id) return;
    onCreate(form);
    setDialogOpen(false);
    setForm({ title: "", description: "", category: "general", priority: "medium", status: "open" });
  };

  const openDrawer = (wo: WorkOrder) => {
    setSelected(wo);
    setDrawerOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    onDelete(deleteTarget.id, {
      onSuccess: () => {
        // Drop the drawer too if it was showing the row we just removed.
        if (selected?.id === deleteTarget.id) {
          setDrawerOpen(false);
          setSelected(null);
        }
        setDeleteTarget(null);
      },
    });
  };

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">Open</p>
            <p className="text-lg font-bold text-destructive">{visibleWorkOrders.filter(w => w.status === "open").length}</p>
          </Card>
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">In Progress</p>
            <p className="text-lg font-bold text-primary">{visibleWorkOrders.filter(w => w.status === "in_progress").length}</p>
          </Card>
          <Card className="px-4 py-2 shadow-soft">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-lg font-bold text-muted-foreground">{visibleWorkOrders.filter(w => w.status === "completed").length}</p>
          </Card>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> New Work Order
        </Button>
      </div>

      {workOrders.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Wrench className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No work orders yet</p>
            <p className="text-sm text-muted-foreground/60">Create your first work order to start tracking maintenance</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-1 min-w-0 w-fit">
            <label className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Listing</label>
            <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
              <SelectTrigger className="h-[34px] min-w-[200px] text-[13px] bg-muted border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All listings</SelectItem>
                {filterOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card className="shadow-soft">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead className="w-[60px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleWorkOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                      No work orders for this listing
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleWorkOrders.map((wo) => {
                    const badge = STATUS_BADGES[wo.status] || STATUS_BADGES.open;
                    return (
                      <TableRow key={wo.id} className="cursor-pointer" onClick={() => openDrawer(wo)}>
                        <TableCell className="font-medium">{wo.title}</TableCell>
                        <TableCell className="capitalize">{wo.category.replace("_", " ")}</TableCell>
                        <TableCell>
                          <Badge variant={wo.priority === "urgent" ? "destructive" : wo.priority === "high" ? "default" : "outline"} className="capitalize">
                            {wo.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={badge.variant} className={cn("gap-1 capitalize", badge.className)}>
                            {badge.icon} {wo.status.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>{wo.vendor_name || "—"}</TableCell>
                        <TableCell>{wo.due_date ? format(new Date(wo.due_date), "MMM d") : "—"}</TableCell>
                        <TableCell>{wo.cost_actual ? `$${wo.cost_actual.toLocaleString()}` : wo.cost_estimate ? `~$${wo.cost_estimate.toLocaleString()}` : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            aria-label={`Delete work order ${wo.title}`}
                            // The row itself opens the drawer — don't let the
                            // delete click bubble into it.
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(wo);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select value={form.pipeline_property_id || ""} onValueChange={(v) => setForm({ ...form, pipeline_property_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {workOrderPropertyOptions.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Fix leaky faucet" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name</Label>
                <Input value={form.vendor_name || ""} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <DateInput value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cost Estimate ($)</Label>
              <Input type="number" value={form.cost_estimate || ""} onChange={(e) => setForm({ ...form, cost_estimate: Number(e.target.value) || null })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!form.title || !form.pipeline_property_id || isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WorkOrderDetailDrawer
        workOrder={selected}
        open={drawerOpen}
        property={selected ? propertyMeta[selected.pipeline_property_id] : undefined}
        onOpenChange={setDrawerOpen}
        onUpdate={onUpdate}
        isSaving={isUpdating}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete this work order?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium text-foreground">{deleteTarget?.title}</span>
            , including its vendor details, costs, and notes. This can't be undone.
          </>
        }
        confirmLabel="Delete work order"
        isPending={isDeleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
